// Gatilhos, chave geral e painel de execução — Fase 11.
//
// O que separa esta fase da 10: a 10 monta o fluxo, esta decide QUANDO ele
// dispara sozinho. É a fase em que o motor passa a agir sem ninguém pedir — e
// por isso ela carrega junto as duas coisas que permitem dormir à noite: a
// chave geral e o painel que mostra onde os runs pararam.

import { supabase } from '../../../src/lib/supabase';
import type { Run } from './tipos';

export type TipoGatilho =
  | 'palavra_chave'
  | 'primeira_mensagem'
  | 'fora_do_horario'
  | 'evento'
  | 'sem_resposta';

export interface Gatilho {
  id: string;
  flow_id: string;
  tipo: TipoGatilho;
  config: Record<string, unknown>;
  /** Menor número dispara primeiro. Ver `PRECEDENCIA` abaixo. */
  prioridade: number;
  ativo: boolean;
  created_at: string;
}

/** Um gatilho com o nome do fluxo, para a tela de precedência. */
export interface GatilhoComFluxo extends Gatilho {
  flow_nome: string;
  flow_ativo: boolean;
}

/**
 * A regra de desempate, escrita uma vez e exibida na tela.
 *
 * O critério de aceite da fase é "dois gatilhos concorrentes resolvem por regra
 * explícita, não por sorte". Sem uma regra escrita, quem resolveria seria a
 * ordem em que o banco devolvesse as linhas — que muda sem avisar e não é
 * observável por quem monta o fluxo.
 */
export const PRECEDENCIA =
  'Quando dois gatilhos combinam com a mesma mensagem, dispara o de MENOR número de '
  + 'prioridade. Empate, vale o gatilho cadastrado primeiro. E só um fluxo começa por '
  + 'mensagem: o segundo colocado não roda.';

export const ROTULO_GATILHO: Record<TipoGatilho, { titulo: string; resumo: string }> = {
  palavra_chave: {
    titulo: 'Palavra-chave',
    resumo: 'O cliente escreveu alguma das palavras da lista.',
  },
  primeira_mensagem: {
    titulo: 'Primeira mensagem do contato',
    resumo: 'É a primeira vez que esse contato escreve nesta conversa.',
  },
  fora_do_horario: {
    titulo: 'Fora do horário',
    resumo: 'Chegou mensagem fora do horário de atendimento.',
  },
  evento: {
    titulo: 'Evento do sistema',
    resumo: 'Um evento do barramento aconteceu (chamado aberto, conversa resolvida).',
  },
  sem_resposta: {
    titulo: 'Sem resposta há X minutos',
    resumo: 'A conversa ficou parada esperando a gente.',
  },
};

export async function carregarGatilhos(flowId: string): Promise<Gatilho[]> {
  const { data, error } = await supabase
    .from('inbox_flow_triggers')
    .select('id, flow_id, tipo, config, prioridade, ativo, created_at')
    .eq('flow_id', flowId)
    .order('prioridade', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as Gatilho[];
}

/** Todos os gatilhos da org, na ORDEM EM QUE DISPARAM. É a tela de precedência. */
export async function carregarGatilhosDaOrg(orgId: string): Promise<GatilhoComFluxo[]> {
  const { data, error } = await supabase
    .from('inbox_gatilhos_ordem')
    .select('*')
    .eq('org_id', orgId)
    // A view já ordena, mas o PostgREST não garante que a ordem da view
    // sobreviva à consulta. Repetir aqui mantém a tela igual à decisão do
    // motor — e a tela existe justamente para mostrar essa ordem.
    .order('prioridade', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as GatilhoComFluxo[];
}

export async function salvarGatilho(
  gatilho: Omit<Gatilho, 'id' | 'created_at'> & { id?: string },
): Promise<void> {
  const linha = {
    flow_id: gatilho.flow_id,
    tipo: gatilho.tipo,
    config: gatilho.config,
    prioridade: gatilho.prioridade,
    ativo: gatilho.ativo,
  };
  const { error } = gatilho.id
    ? await supabase.from('inbox_flow_triggers').update(linha).eq('id', gatilho.id)
    : await supabase.from('inbox_flow_triggers').insert(linha);
  if (error) throw new Error(error.message);
}

export async function apagarGatilho(id: string): Promise<void> {
  const { error } = await supabase.from('inbox_flow_triggers').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Chave geral ────────────────────────────────────────────────────────────

export async function lerMotorGeral(orgId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('inbox_orgs')
    .select('motor_fluxo_ligado')
    .eq('id', orgId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data?.motor_fluxo_ligado ?? false;
}

/**
 * Desliga a automação da organização inteira.
 *
 * O motor relê esta linha A CADA PASSO, nunca de cache: é o que faz a chave ter
 * efeito às 22h de um sábado, sem deploy e sem reiniciar worker.
 */
export async function ligarMotorGeral(orgId: string, ligado: boolean): Promise<void> {
  const { error } = await supabase
    .from('inbox_orgs')
    .update({ motor_fluxo_ligado: ligado })
    .eq('id', orgId);
  if (error) throw new Error(error.message);
}

/**
 * Interrompe os runs em andamento.
 *
 * Separado de desligar de propósito (decisão da Fase 9): desligar impede novos
 * disparos, interromper abandona quem já está no meio da conversa. Juntar os
 * dois tiraria de quem opera a chance de escolher — e às vezes a escolha certa
 * é deixar terminar o que já começou.
 */
export async function pararRuns(orgId: string, motivo: string): Promise<number> {
  const { data, error } = await supabase.rpc('inbox_parar_runs', { p_org_id: orgId, p_motivo: motivo });
  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}

export async function pararRunsDoFluxo(flowId: string, motivo: string): Promise<number> {
  const { data, error } = await supabase.rpc('inbox_parar_runs_do_fluxo', {
    p_flow_id: flowId,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}

// ─── Painel de execução ─────────────────────────────────────────────────────

export interface ResumoRuns {
  flow_id: string;
  ativos: number;
  aguardando: number;
  concluidos: number;
  falhados: number;
  interrompidos: number;
  ultimo_em: string | null;
}

export async function carregarResumoRuns(orgId: string): Promise<ResumoRuns[]> {
  const { data, error } = await supabase.from('inbox_fluxo_resumo').select('*').eq('org_id', orgId);
  if (error) throw new Error(error.message);
  return (data ?? []) as ResumoRuns[];
}

/** Os runs que pararam mal — o que o painel precisa mostrar primeiro. */
export async function carregarRunsProblematicos(orgId: string, limite = 30): Promise<Run[]> {
  const { data, error } = await supabase
    .from('inbox_flow_runs')
    .select('id, flow_id, version_id, conversation_id, current_node, status, mensagens_enviadas, retomar_em, motivo_parada, created_at, updated_at')
    .eq('org_id', orgId)
    .in('status', ['falhou', 'interrompido'])
    .order('updated_at', { ascending: false })
    .limit(limite);
  if (error) throw new Error(error.message);
  return (data ?? []) as Run[];
}

// ─── Simulação ──────────────────────────────────────────────────────────────

export interface PassoSimulado {
  noKey: string | null;
  registro: string;
  /** O que ACONTECERIA. Nada disso é executado. */
  efeitos: { tipo: string; descricao: string }[];
}

export interface ResultadoSimulacao {
  ok: boolean;
  passos: PassoSimulado[];
  caminho: string[];
  status: string;
  motivo: string | null;
  /** Quantas mensagens o fluxo teria mandado para o cliente. */
  mensagens: number;
}

/**
 * Roda o fluxo sem mandar nada para ninguém.
 *
 * A simulação acontece na edge function, e não aqui no navegador, por um motivo
 * que é o ponto inteiro da funcionalidade: ela usa o MESMO `avancar()` que o
 * executor de produção usa. Uma cópia da máquina de estados em JavaScript do
 * front simularia um motor parecido — e "parecido" é justamente o que não
 * serve, porque o que se quer provar é como o motor de verdade se comporta.
 */
export async function simular(
  versionId: string,
  respostas: string[],
): Promise<ResultadoSimulacao> {
  const { data, error } = await supabase.functions.invoke('inbox-fluxo-simular', {
    body: { version_id: versionId, respostas },
  });
  if (error) {
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalhe?.error || error.message || 'Falha ao simular.');
  }
  return data as ResultadoSimulacao;
}
